import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApi, errMsg } from "@/lib/api";
import { LocationPicker } from "@/components/directory/LocationPicker";
import { DirectoryFields } from "@/components/directory/DirectoryFields";

const KEYS = ["name", "name_ar", "first_name", "last_name", "category", "subcategory", "specialty", "address", "phone", "email", "website", "opening_hours", "wilaya", "commune"];
const empty = (item) => ({ ...Object.fromEntries(KEYS.map((k) => [k, item?.[k] ?? ""])), lat: item?.lat ?? null, lng: item?.lng ?? null, verified: item?.id ? !!item.verified : true });

export const DirectoryForm = ({ item, meta, writer, deleter, onClose, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState(empty(null));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const isNew = item && !item.id;
  useEffect(() => { if (item) { setF(empty(item)); } }, [item]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const submit = (e) => {
    e.preventDefault();
    const body = { ...Object.fromEntries(KEYS.map((k) => [k, f[k]?.trim?.() || null])), lat: f.lat, lng: f.lng, verified: f.verified };
    run(() => (isNew ? api("post", "/sp/directory", body) : api("put", `/sp/directory/${item.id}`, body)), isNew ? "Praticien ajouté à l'annuaire Sehatic-Pedia" : "Fiche mise à jour dans Sehatic-Pedia");
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="directory-form">
        <DialogHeader>
          <DialogTitle>{isNew ? "Ajouter un praticien" : f.name || "Fiche"}</DialogTitle>
          <DialogDescription>{isNew ? "La fiche est publiée immédiatement dans l'annuaire de l'application Sehatic-Pedia." : `Source : ${item?.source === "sehatic-gestion" ? "ajoutée depuis la gestion" : item?.source || "—"}`}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <fieldset disabled={!writer || busy} className="space-y-5">
            <DirectoryFields f={f} setF={setF} meta={meta} />
            <LocationPicker lat={f.lat} lng={f.lng} readOnly={!writer} onPick={(lat, lng) => setF((p) => ({ ...p, lat, lng }))} />
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.verified} onCheckedChange={(v) => setF((p) => ({ ...p, verified: v }))} data-testid="directory-verified-switch" /> Fiche vérifiée (badge visible dans l'application)</label>
          </fieldset>
          <div className="flex flex-wrap gap-2">
            {deleter && !isNew && <Button type="button" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => setConfirm(true)} disabled={busy} data-testid="directory-delete-button">Supprimer</Button>}
            <Button type="button" variant="outline" className="ml-auto" onClick={onClose} data-testid="directory-form-cancel">{writer ? "Annuler" : "Fermer"}</Button>
            {writer && <Button type="submit" disabled={busy} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid="directory-form-submit">{isNew ? "Publier dans l'annuaire" : "Enregistrer"}</Button>}
          </div>
        </form>
        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Supprimer cette fiche ?</AlertDialogTitle><AlertDialogDescription>Elle sera retirée de l'annuaire de l'application Sehatic-Pedia et placée dans la corbeille (restaurable).</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="directory-delete-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction className="bg-red-700 hover:bg-red-800" data-testid="directory-delete-confirm" onClick={() => run(() => api("delete", `/sp/directory/${item.id}`), "Fiche supprimée de l'annuaire")}>Supprimer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
